import { useState, useEffect, useRef } from "react";
import apiClient from "../API/apiClient";
import { useReportingYear } from "../../context/ReportingYearContext";

const TopBar = ({ user, onLogout, onToggleSidebar }) => {
  const { setReportingYear, setReportingYearId } = useReportingYear();
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // ✅ Fetch Years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await apiClient.post("/api/reportingyear");
        const data = response.data;

        if (data.code === 200 && data.data) {
          setYears(data.data);

          let selected = data.data.find((y) => y.is_selected === 1);

// If nothing is selected, pick the latest unlocked reporting year
if (!selected) {
  selected = data.data
    .filter((y) => y.is_locked !== 1)
    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))[0];
}

// If all years are locked, pick the latest one
if (!selected && data.data.length) {
  selected = [...data.data].sort(
    (a, b) => new Date(b.start_date) - new Date(a.start_date)
  )[0];
}

if (selected) {
  setYear(selected);

  const match = selected.year_label.match(/(\d{4}-\d{2,4})/);
  const yearValue = match ? match[1] : selected.year_label;

  setReportingYear(yearValue);
  setReportingYearId(selected.id);
}
        }
      } catch (err) {
        console.error("Failed to fetch fiscal years:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchYears();
  }, []);

  // ✅ Close dropdown outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleYearSelect = (y) => {
    setYear(y);
    setOpen(false);

    const match = y.year_label.match(/(\d{4}-\d{2,4})/);
    const yearValue = match ? match[1] : y.year_label;

    setReportingYear(yearValue); // string
    setReportingYearId(y.id); // id ✅
  };

  const displayName = user?.name || user?.email || "Compliance Officer";

  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="topbar-global">
      {/* ✅ LEFT SECTION */}
      <div className="topbar-left">
        {/* ✅ HAMBURGER (Mobile only) */}
        <button className="hamburger" onClick={onToggleSidebar}>
          ☰
        </button>

        <span className="topbar-label">Reporting Year:</span>

        {/* Dropdown */}
        <div className="year-dropdown-wrap" ref={dropdownRef}>
          <button
            className="year-dropdown-btn"
            onClick={() => setOpen((o) => !o)}
            disabled={loading}
          >
            {loading ? "Loading..." : year?.year_label ?? "Select Year"}
            <span className="dropdown-arrow">▾</span>
          </button>

          {open && (
            <div className="year-dropdown-menu">
              {years.map((y) => (
                <div
                  key={y.id}
                  className={`year-option ${
                    y.id === year?.id ? "selected" : ""
                  }`}
                  onClick={() => handleYearSelect(y)}
                >
                  <span className="year-option-check">
                    {y.id === year?.id ? "✓" : ""}
                  </span>

                  <span style={{ flex: 1 }}>{y.year_label}</span>

                  {y.is_locked === 1 && <span className="lock-icon">🔒</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ✅ RIGHT SECTION */}
      <div className="topbar-right">
        <span className="topbar-username">{displayName}</span>

        <div className="topbar-avatar">{initials}</div>

        <button
          className="topbar-logout-icon"
          title="Sign out"
          onClick={onLogout}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TopBar;
